import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Calendar as CalendarIcon, FileText, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface GenerateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (config: ReportConfig) => void;
}

export interface ReportConfig {
  reportType: "consolidated" | "individual";
  analysisType: "single" | "comparative";
  startDate: Date | undefined;
  endDate: Date | undefined;
  compareDate: Date | undefined;
  breakdowns: string[];
  assetClasses: string[];
  includeCharts: boolean;
  includeTables: boolean;
  watermark: string;
  expiryDays: number;
}

export function GenerateReportDialog({ open, onOpenChange, onGenerate }: GenerateReportDialogProps) {
  const [reportType, setReportType] = useState<"consolidated" | "individual">("consolidated");
  const [analysisType, setAnalysisType] = useState<"single" | "comparative">("single");
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [compareDate, setCompareDate] = useState<Date | undefined>();
  const [breakdowns, setBreakdowns] = useState<string[]>(["asset_class", "currency"]);
  const [assetClasses, setAssetClasses] = useState<string[]>(["all"]);
  const [watermark, setWatermark] = useState("");
  const [expiryDays, setExpiryDays] = useState(30);

  const handleGenerate = () => {
    onGenerate({
      reportType,
      analysisType,
      startDate,
      endDate,
      compareDate,
      breakdowns,
      assetClasses,
      includeCharts: true,
      includeTables: true,
      watermark,
      expiryDays,
    });
    onOpenChange(false);
  };

  const toggleBreakdown = (breakdown: string) => {
    setBreakdowns(prev =>
      prev.includes(breakdown)
        ? prev.filter(b => b !== breakdown)
        : [...prev, breakdown]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Generate Portfolio Report
          </DialogTitle>
          <DialogDescription>
            Configure comprehensive report with analytics, breakdowns, and export options
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Report Type */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Report Level</Label>
            <RadioGroup value={reportType} onValueChange={(v) => setReportType(v as "consolidated" | "individual")}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="consolidated" id="consolidated" />
                <Label htmlFor="consolidated" className="font-normal cursor-pointer">
                  Consolidated Portfolio
                  <span className="block text-xs text-muted-foreground">
                    All assets across entities and accounts
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="individual" id="individual" />
                <Label htmlFor="individual" className="font-normal cursor-pointer">
                  Individual Asset Level
                  <span className="block text-xs text-muted-foreground">
                    Detailed breakdown by specific assets
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* Analysis Type */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Analysis Type</Label>
            <RadioGroup value={analysisType} onValueChange={(v) => setAnalysisType(v as "single" | "comparative")}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="single" />
                <Label htmlFor="single" className="font-normal cursor-pointer">
                  Single Date Snapshot
                  <span className="block text-xs text-muted-foreground">
                    Point-in-time analysis
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="comparative" id="comparative" />
                <Label htmlFor="comparative" className="font-normal cursor-pointer">
                  Comparative Analysis
                  <span className="block text-xs text-muted-foreground">
                    Compare across time periods
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* Date Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Date Range</Label>
            <div className="grid gap-4">
              {analysisType === "single" ? (
                <div className="space-y-2">
                  <Label htmlFor="as-of-date">As of Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="as-of-date"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="h-4 w-4" />
                        {startDate ? format(startDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start-date">Period Start</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="start-date"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !startDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="h-4 w-4" />
                            {startDate ? format(startDate, "PPP") : "Start"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={startDate}
                            onSelect={setStartDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end-date">Period End</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="end-date"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !endDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="h-4 w-4" />
                            {endDate ? format(endDate, "PPP") : "End"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={endDate}
                            onSelect={setEndDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="compare-date">Compare To (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="compare-date"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !compareDate && "text-muted-foreground"
                          )}
                        >
                          <TrendingUp className="h-4 w-4" />
                          {compareDate ? format(compareDate, "PPP") : "Select comparison date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={compareDate}
                          onSelect={setCompareDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}
            </div>
          </div>

          <Separator />

          {/* Breakdowns */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Include Breakdowns</Label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "asset_class", label: "Asset Class" },
                { id: "currency", label: "Currency" },
                { id: "geography", label: "Geography" },
                { id: "sector", label: "Sector" },
                { id: "entity", label: "Entity" },
                { id: "custodian", label: "Custodian" },
              ].map((breakdown) => (
                <div key={breakdown.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={breakdown.id}
                    checked={breakdowns.includes(breakdown.id)}
                    onCheckedChange={() => toggleBreakdown(breakdown.id)}
                  />
                  <Label htmlFor={breakdown.id} className="font-normal cursor-pointer">
                    {breakdown.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Asset Class Filter */}
          <div className="space-y-3">
            <Label htmlFor="asset-classes" className="text-base font-semibold">
              Filter Asset Classes
            </Label>
            <Select defaultValue="all">
              <SelectTrigger id="asset-classes">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Asset Classes</SelectItem>
                <SelectItem value="equity">Public Equity</SelectItem>
                <SelectItem value="fixed_income">Fixed Income</SelectItem>
                <SelectItem value="alternatives">Alternatives</SelectItem>
                <SelectItem value="cash">Cash & Equivalents</SelectItem>
                <SelectItem value="real_estate">Real Estate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Export Settings */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Export Settings</Label>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="watermark">Watermark Text (Optional)</Label>
                <Input
                  id="watermark"
                  placeholder="e.g., Confidential - Smith Family Office"
                  value={watermark}
                  onChange={(e) => setWatermark(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiry">Report Expiry (Days)</Label>
                <Select value={expiryDays.toString()} onValueChange={(v) => setExpiryDays(Number(v))}>
                  <SelectTrigger id="expiry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 Days</SelectItem>
                    <SelectItem value="14">14 Days</SelectItem>
                    <SelectItem value="30">30 Days</SelectItem>
                    <SelectItem value="60">60 Days</SelectItem>
                    <SelectItem value="90">90 Days</SelectItem>
                    <SelectItem value="0">No Expiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleGenerate}>
            Generate Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
